/**
 * Zod schemas shared between server (request validation) and web (form validation).
 *
 * NOTE: We intentionally do NOT call `extendZodWithOpenApi(z)` here.
 * Reason: that helper mutates the Zod JSON-Schema output (`required` becomes
 * `boolean` per-field), which breaks `fastify-type-provider-zod`/Ajv
 * validation (`data/required must be array`).
 *
 * OpenAPI generation is handled separately in apps/server/src/plugins/swagger.ts
 * by manually composing the spec from a registry + JSON schema dumps from
 * `zod-to-json-schema` for component types.
 */
import { z } from 'zod';
// =============================================================================
// Auth schemas
// =============================================================================
export const LoginRequestSchema = z.object({
    username: z.string().min(3).max(64),
    password: z.string().min(8).max(128),
});
export const LoginResponseSchema = z.object({
    id: z.number().int().positive(),
    username: z.string(),
    role: z.enum(['viewer', 'admin', 'super']),
    expiresAt: z.string(),
    // Admin JWT — returned so the SPA can include it as a query param on
    // cross-origin <img src="/screenshots/..."> requests. httpOnly cookies
    // are not sent by <img>, so the token has to travel in the URL.
    token: z.string(),
});
// =============================================================================
// Employee schemas
// =============================================================================
export const AgentInfoSchema = z.object({
    id: z.number().int().positive(),
    hostname: z.string(),
    osVersion: z.string(),
    ipAddress: z.string().nullable().optional(),
    lastHeartbeat: z.string().nullable(),
    isOnline: z.boolean(),
    agentVersion: z.string().nullable(),
});
export const EmployeeSchema = z.object({
    id: z.number().int().positive(),
    employeeCode: z.string().min(1).max(32),
    fullName: z.string().min(1).max(128),
    department: z.string().max(64).nullable().optional(),
    position: z.string().max(64).nullable().optional(),
    email: z.string().email().nullable().optional(),
    isActive: z.boolean(),
    createdAt: z.string(),
    agent: AgentInfoSchema.nullable().optional(),
});
export const CreateEmployeeRequestSchema = EmployeeSchema.pick({
    employeeCode: true,
    fullName: true,
    department: true,
    position: true,
    email: true,
});
export const UpdateEmployeeRequestSchema = CreateEmployeeRequestSchema.partial().extend({
    isActive: z.boolean().optional(),
});
export const EmployeeListSchema = z.object({
    items: z.array(EmployeeSchema),
    total: z.number().int(),
});
// =============================================================================
// Agent schemas
// =============================================================================
export const AgentRegisterRequestSchema = z.object({
    agentCode: z.string().min(1).max(32),
    hostname: z.string().min(1).max(255),
    osVersion: z.string().min(1).max(128),
    agentVersion: z.string().min(1).max(32).default('0.1.0'),
});
export const AgentConfigSchema = z.object({
    screenshotIntervalSeconds: z.number().int().min(1).max(300),
    heartbeatIntervalSeconds: z.number().int().min(10).max(600),
    features: z.object({
        screenshotEnabled: z.boolean(),
        activityTrackingEnabled: z.boolean(),
        urlTrackingEnabled: z.boolean(),
    }),
    latestAgentVersion: z.string().optional(),
    agentDownloadUrl: z.string().min(1).optional(),
});
export const AgentRegisterResponseSchema = z.object({
    agentToken: z.string().min(32),
    employeeId: z.number().int().positive(),
    employeeCode: z.string(),
    config: AgentConfigSchema,
});
export const HeartbeatRequestSchema = z.object({
    agentVersion: z.string().min(1).max(32),
    ipAddress: z.string().optional(),
});
export const HeartbeatResponseSchema = z.object({
    ack: z.literal(true),
    config: AgentConfigSchema.optional(),
});
/** Graceful disconnect: identify agent by code (no token required —
 *  this endpoint only flips a single boolean and is idempotent, so it's
 *  safe to call unauthenticated; same trade-off as /agent/register). */
export const AgentDisconnectRequestSchema = z.object({
    agentCode: z.string().min(1).max(32),
    reason: z.string().max(64).default('agent_requested'),
});
export const AgentDisconnectResponseSchema = z.object({
    ok: z.literal(true),
    reason: z.string(),
});
export const ActivityRequestSchema = z.object({
    appName: z.string().max(256).nullable().optional(),
    windowTitle: z.string().max(512).nullable().optional(),
    url: z.string().url().max(2048).nullable().optional(),
    isIdle: z.boolean().default(false),
    idleSeconds: z.number().int().min(0).max(86400).default(0),
});
export const ScreenshotMetaSchema = z.object({
    // id=0 means "no DB row was created" — used by the transient + deduped
    // responses where the server streamed the frame via Socket.IO without
    // persisting anything. Positive means a real ScreenshotMeta row.
    id: z.number().int().nonnegative(),
    employeeId: z.number().int().positive(),
    capturedAt: z.string(),
    url: z.string(),
    // width / height / fileSize = 0 means the server didn't actually decode
    // the frame (transient path uses the capturedAt timestamp + phash to
    // represent the frame; the dashboard's <img> falls back to the cached
    // live-frame when these are 0).
    width: z.number().int().nonnegative(),
    height: z.number().int().nonnegative(),
    fileSize: z.number().int().nonnegative(),
});
export const ScreenshotUploadResponseSchema = z.object({
    ok: z.literal(true),
    meta: ScreenshotMetaSchema,
    // When the dashboard is offline OR persistence is 'transient', the server
    // streams the frame through Socket.IO but doesn't write it to disk or DB.
    // We surface that to the agent so its upload log can distinguish "frame
    // delivered" from "frame persisted". Optional because the normal
    // (live+admin-online) path doesn't set either flag.
    transient: z.literal(true).optional(),
    // phash-collapsed frame: identical JPEG to the previous one. Agent keeps
    // uploading so the BE knows the loop is alive, but no file or row is
    // created. Optional for the same reason as `transient`.
    deduped: z.literal(true).optional(),
});
// =============================================================================
// Realtime event payloads (Socket.IO)
// =============================================================================
export const AgentOnlineEventSchema = z.object({
    employeeId: z.number().int().positive(),
    lastHeartbeat: z.string(),
});
export const AgentOfflineEventSchema = z.object({
    employeeId: z.number().int().positive(),
    lastHeartbeat: z.string().nullable(),
    reason: z.string().optional(),
    at: z.string().optional(),
});
export const ScreenNewEventSchema = z.object({
    employeeId: z.number().int().positive(),
    meta: ScreenshotMetaSchema,
    activity: z
        .object({
        appName: z.string().nullable(),
        windowTitle: z.string().nullable(),
        url: z.string().nullable(),
    })
        .nullable()
        .optional(),
    // Server signals when this payload carries the same frame as before
    // (perceptual-hash dedup hit, or connect-time replay). Lets the client
    // distinguish a fresh capture from a heartbeat so UIs can avoid
    // invalidating caches / burning reload animations.
    deduped: z.boolean().optional(),
    // True when this frame is being broadcast live but NOT persisted to
    // disk + DB because no admin dashboard is currently open. The FE can
    // either render it once and forget, or render it as a "live only"
    // badge. meta.url is empty in this mode.
    transient: z.boolean().optional(),
    // Zero-cost realtime: JPEG bytes as base64, embedded directly in the
    // socket event so dashboard + fly-receiver render/save WITHOUT any
    // Fly disk/DB write and WITHOUT an extra HTTP fetch to
    // /api/admin/live-frames. Optional for backward compat (old servers
    // omit it, old clients ignore it). Omitted when frame > ~700KB to
    // stay under Socket.IO maxPayload (1MB).
    dataBase64: z.string().optional(),
});
// =============================================================================
// Remote control — dashboard → agent commands
// =============================================================================
export const CommandSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('mouse_move'), x: z.number().int(), y: z.number().int() }),
    z.object({
        type: z.literal('mouse_click'),
        x: z.number().int(),
        y: z.number().int(),
        button: z.enum(['left', 'right', 'middle']).default('left'),
        count: z.number().int().min(1).max(3).default(1),
    }),
    z.object({
        type: z.literal('mouse_scroll'),
        x: z.number().int(),
        y: z.number().int(),
        dx: z.number().int(),
        dy: z.number().int(),
    }),
    z.object({
        type: z.literal('key_tap'),
        key: z.string().min(1).max(64),
    }),
    z.object({
        type: z.literal('type_text'),
        text: z.string().min(1).max(4096),
    }),
    z.object({ type: z.literal('screenshot_now') }),
]);
export const AgentCommandEventSchema = z.object({
    commandId: z.string().uuid(),
    employeeId: z.number().int().positive(),
    command: CommandSchema,
    issuedAt: z.string(),
});
export const AgentCommandResultSchema = z.object({
    commandId: z.string().uuid(),
    employeeId: z.number().int().positive(),
    ok: z.boolean(),
    error: z.string().optional(),
    capturedAt: z.string().optional(), // for screenshot_now
    finishedAt: z.string(),
});
// =============================================================================
// Generic helpers
// =============================================================================
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const DateRangeQuerySchema = z.object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
export const ErrorResponseSchema = z.object({
    statusCode: z.number().int(),
    error: z.string(),
    message: z.string(),
});
// =============================================================================
// Admin diagnostics — per-employee agent liveness snapshot
// =============================================================================
//
// Aggregated view used by the /diagnostics admin page. Joins:
//   Employee + Agent (full row, minus agentToken) +
//   last screenshot timestamp + last activity timestamp + 24h counts.
// The whole point is to let an admin distinguish "agent is alive but DB/cache
// lagged" from "agent is dead on the employee machine" without RDP-ing into
// every box.
export const AgentDiagSchema = z.object({
    // Employee subset
    id: z.number().int().positive(),
    employeeCode: z.string(),
    fullName: z.string(),
    department: z.string().nullable().optional(),
    position: z.string().nullable().optional(),
    isActive: z.boolean(),
    // Agent fields (no agentToken — never expose)
    agentId: z.number().int().nullable(),
    hostname: z.string().nullable(),
    osVersion: z.string().nullable(),
    agentVersion: z.string().nullable(),
    ipAddress: z.string().nullable(),
    registeredAt: z.string().nullable(),
    lastHeartbeat: z.string().nullable(),
    isOnline: z.boolean(),
    isIdle: z.boolean(),
    currentApp: z.string().nullable(),
    currentTitle: z.string().nullable(),
    currentUrl: z.string().nullable(),
    // Aggregates joined from ScreenshotMeta + ActivityLog
    lastScreenshotAt: z.string().nullable(),
    lastActivityAt: z.string().nullable(),
    screenshotCount24h: z.number().int().nonnegative(),
    activityCount24h: z.number().int().nonnegative(),
    // Derived server-side: how long since ANY agent signal (lastHeartbeat).
    // null when the agent has never reported in.
    secsSinceLastSignal: z.number().int().nullable(),
});
export const AgentDiagSummarySchema = z.object({
    total: z.number().int().nonnegative(),
    // Ready: any signal in the last 2 minutes — agent is alive on the machine.
    online: z.number().int().nonnegative(),
    // No signal for > 5 minutes — agent is either dead or the machine is off.
    offline: z.number().int().nonnegative(),
    // DB says isOnline=true but lastHeartbeat is > 5 min: the offline-job
    // hasn't run yet, or heartbeat is failing. Worth a follow-up.
    stale: z.number().int().nonnegative(),
});
export const AgentDiagListSchema = z.object({
    items: z.array(AgentDiagSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    // KPI block for the dashboard top row. Computed over every agent in
    // the DB (not just the current page) so the cards always reflect the
    // whole fleet.
    summary: AgentDiagSummarySchema,
});
export const AgentDiagDetailSchema = AgentDiagSchema.extend({
    recentScreenshots: z.array(z.object({
        id: z.number().int().positive(),
        capturedAt: z.string(),
        url: z.string(),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
    })),
    recentActivity: z.array(z.object({
        id: z.number().int().positive(),
        loggedAt: z.string(),
        appName: z.string().nullable(),
        windowTitle: z.string().nullable(),
        url: z.string().nullable(),
        isIdle: z.boolean(),
    })),
});
export const AgentDiagQuerySchema = PaginationQuerySchema.extend({
    search: z.string().optional(),
    isOnline: z.coerce.boolean().optional(),
    department: z.string().optional(),
});
// =============================================================================
// Agent connection event log — admin-facing audit trail
// =============================================================================
//
// Each row is one observable event in the agent <-> BE conversation:
//   - register           : /agent/register success → BE issued a token
//   - register_failed    : /agent/register rejected (unknown agentCode etc.)
//   - auth_failed        : requireAgent middleware rejected a token
//   - heartbeat          : sampled /agent/heartbeat (1-in-N, not every call)
//   - screenshot_skip    : uploaded frame was deduped against the prior one
//                          (recorded lightly so admins see dedup-rate health)
//
// We do NOT log every screenshot upload (would be ~30 rows/NV/minute). The
// dashboard heartbeat is sampled — heartbeat rows are the proof that an agent
// reached the BE recently even if its screenshots are dedup'd or paused.
//
// Volume target: < 50 rows/NV/day, capped via heartbeat sampling.
export const AgentConnectionEventTypeSchema = z.enum([
    'register',
    'register_failed',
    'auth_failed',
    'heartbeat',
    'screenshot_skip',
    'disconnect',
]);
export const AgentConnectionEventSchema = z.object({
    id: z.number().int().positive(),
    // Null for register_failed / auth_failed where the token didn't resolve
    // to a known Employee. Admin can still grep by employeeCode below.
    employeeId: z.number().int().nullable(),
    employeeCode: z.string().nullable(),
    eventType: AgentConnectionEventTypeSchema,
    ok: z.boolean(),
    hostname: z.string().nullable(),
    agentVersion: z.string().nullable(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
});
export const AgentConnectionEventListSchema = z.object({
    items: z.array(AgentConnectionEventSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    summary: z.object({
        total: z.number().int().nonnegative(),
        failures: z.number().int().nonnegative(),
        lastRegisterAt: z.string().nullable(),
        lastAuthFailedAt: z.string().nullable(),
    }),
});
export const AgentConnectionEventQuerySchema = PaginationQuerySchema.extend({
    employeeId: z.coerce.number().int().positive().optional(),
    eventType: AgentConnectionEventTypeSchema.optional(),
    ok: z.coerce.boolean().optional(),
    search: z.string().optional(),
});
//# sourceMappingURL=index.js.map